import { faChevronLeft, faPenClip } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import cieIcon from "../assets/svg/cie-icon.svg";
import faceId from "../assets/svg/face-id.svg";
import spidIcon from "../assets/svg/spid-icon.svg";
import LinearFooterCmp from "../components/footer/LinearFooter";
import LoaderCmp from "../components/loader/LoaderCmp";
import { NextButton, SecondChoiseButton } from "../components/styled/Button.style";
import { LTitleIconSection, Section } from "../components/styled/Containers.style";
import { LIconContainer } from "../components/styled/Icon.style";
import { Vspace } from "../components/styled/Separators.style";
import { LPageTitle, LSimpleText, PageTitle, TextDefault } from "../components/styled/Text.style";
import { AUTH_CONFIRM_BASE_ROUTH, AUTH_CONFIRM_PATHS } from "../routes/AuthConfirmRouter";
import { PATHS } from "../routes/CaiExperienceRouter";
import { LINEAR_BASE_ROUTE, LINEAR_PATHS } from "../routes/linearRouteConstants";
import { selectCommunicationData } from "../store";
import { useAppSelector } from "../store/hooks";
import { TemplateTypeEnum } from "../types/commonTypes";
import { CaiCaseEnum, SignAuthType, UserRoleEnum, UserSignResEnum, ViewerLink } from "../types/communication/communicationDataType";
import { composeUrl } from "../utils/utils";
import PageContainerCmp from "./PageContainerCmp";
import { ToastNotifyError } from '../store/communication-data/sagas';

export type ExpiredOutcomeNavigation = {
    nextPath: string;
    baseRoute?: string;
    url: string;
};

export const getExpiredOutcomeNavigation = (params: {
    isLinear: boolean;
    token: string;
    userRole: UserRoleEnum;
    caiCaseEnum?: CaiCaseEnum;
    events?: Array<any>;
    composeUrlFn?: (token: string, stepUrl: string, baseRoute?: string) => string;
}): ExpiredOutcomeNavigation | undefined => {
    const {
        isLinear,
        token,
        userRole,
        caiCaseEnum,
        events,
        composeUrlFn = composeUrl
    } = params;

    const hasExpiredOutcome = events?.some((event: any) =>
        event?.metadata?.outcome === UserSignResEnum.CAI_REFUSED ||
        event?.metadata?.outcome === UserSignResEnum.NOT_EDITED
    );

    if (!hasExpiredOutcome) {
        return undefined;
    }

    //unipol
    let nextPath = (userRole === UserRoleEnum.USER_1) ?
        PATHS.TOKEN_EXPIRED :
        AUTH_CONFIRM_PATHS.AUTHORIZE_CONFIRM_TOKEN_EXPIRED;
    let baseRoute = (userRole === UserRoleEnum.USER_1) ? undefined : AUTH_CONFIRM_BASE_ROUTH;
    if (caiCaseEnum === CaiCaseEnum.CASE_2) {
        nextPath = AUTH_CONFIRM_PATHS.AUTHORIZE_CONFIRM_TOKEN_EXPIRED;
        baseRoute = AUTH_CONFIRM_BASE_ROUTH;
    }

    // linear
    if (isLinear) {
        nextPath = (userRole === UserRoleEnum.USER_1) ?
            LINEAR_PATHS.TOKEN_EXPIRED :
            AUTH_CONFIRM_PATHS.AUTHORIZE_CONFIRM_TOKEN_EXPIRED;
        baseRoute = `${LINEAR_BASE_ROUTE}/${AUTH_CONFIRM_BASE_ROUTH}`
    }

    const url = composeUrlFn(token, nextPath, baseRoute);

    return {
        nextPath,
        baseRoute,
        url
    };
};

const ChooseSignatureAuthenticationPage = () => {

    const communicationData = useAppSelector(selectCommunicationData)
    const { communication } = communicationData;
    const { token, userRole, caiCaseEnum, linearControparteInfo } = communication;
    const { agreeWithCtp } = linearControparteInfo || {}
    const [localLoader, setLocalLoader] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams();
    const isLinear = communication?.companyName === "LINEAR";
    const navigate = useNavigate();

    const handleExpiredOutcome = () => {
        if (!userRole) {
            return false;
        }
        const navigation = getExpiredOutcomeNavigation({
            isLinear,
            token,
            userRole,
            caiCaseEnum,
            events: communication?.events
        });
        if (!navigation) {
            return false;
        }
        navigate(navigation.url);
        return true;
    }

    useEffect(() => {
        const feaFeqQuery = searchParams.get('exit')
        if (feaFeqQuery === "true") {
            let newUrl: string | null = null;
            let nextPath: string | null = null;
            if (!isLinear) {
                nextPath = (userRole === UserRoleEnum.USER_1) ?
                    PATHS.END_SIGNATURE_FORWARD_SUCCESS :
                    AUTH_CONFIRM_PATHS.AUTHORIZE_END_SIGNATURE_FORWARD_SUCCESS;
                newUrl = composeUrl(token, nextPath, (userRole === UserRoleEnum.USER_1) ? undefined : AUTH_CONFIRM_BASE_ROUTH);
                //eccezione per CASE_2
                if (caiCaseEnum === CaiCaseEnum.CASE_2) {
                    nextPath = AUTH_CONFIRM_PATHS.AUTHORIZE_END_SIGNATURE_FORWARD_SUCCESS;
                    newUrl = composeUrl(token, nextPath, AUTH_CONFIRM_BASE_ROUTH);
                }
            } else {
                nextPath = LINEAR_PATHS.LINEAR_REALTIME_ACCETTAZIONE_CAI_PAGE;
                newUrl = composeUrl(token, nextPath, LINEAR_BASE_ROUTE);
                if (communication?.userRole === UserRoleEnum.CTP) {
                    nextPath = AUTH_CONFIRM_PATHS.AUTHORIZE_LINEAR_REALTIME_ACCETTAZIONE_CAI_PAGE;
                    const baseRoute = `${LINEAR_BASE_ROUTE}/${AUTH_CONFIRM_BASE_ROUTH}`
                    newUrl = composeUrl(token, nextPath, baseRoute)
                }
            }

            // Rimuovi il parametro 'exit' dalla query string usando setSearchParams
            setSearchParams((prevParams) => {
                const newSearchParams = new URLSearchParams(prevParams);
                newSearchParams.delete('exit');

                // conserva tutte le altre url query parameters
                if (newUrl && newSearchParams?.toString()) {
                    newUrl += (newUrl.includes('?') ? '&' : '?') + newSearchParams.toString();
                }
                newUrl && navigate(newUrl);

                return newSearchParams;
            });
        }
        document.addEventListener('visibilitychange', () => setLocalLoader(false));
        return () => {
            document.removeEventListener('visibilitychange', () => setLocalLoader(false));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /* istanbul ignore next */
    const callForSignature = async (value?: SignAuthType) => {
        if (handleExpiredOutcome()) {
            return;
        }
        setLocalLoader(true);
        try {
            // 1. se non esistono authType precedenti chiama la firma per la sua prima volta, e attendi l'apertura della pagina in callback
            // 2. altrimenti chiama sempre l'api per la replace activity che ti da un nuovo url
            let res = null;
            let savedLink: ViewerLink | undefined = communication.signatureInfo?.viewerLinks?.find((el: ViewerLink) => el.userRole === communication.userRole);
            if (!savedLink?.authType) {
                let requestURL = `${window.REACT_APP_VTV_API_ENDPOINT}/api/realtime/communication/${communication.token}/start-signature-process?authType=${value}`;
                if (isLinear && !agreeWithCtp) requestURL += `&mono=true`;
                res = await axios.get(requestURL)
            } else {
                let requestURL = `${window.REACT_APP_VTV_API_ENDPOINT}/api/realtime/communication/${communication.token}/change-user-auth`;
                let savedLink: ViewerLink | undefined = communication.signatureInfo?.viewerLinks?.find((el: ViewerLink) => el.userRole === communication.userRole);
                res = await axios.post(requestURL, {
                    currentViewerLink: savedLink,
                    envelopeId: communication?.signatureInfo?.envelopeId,
                    newAuthType: value
                })
            }
            if (res?.data) {
                let callbackResponseLink: string | undefined = res.data?.data?.viewerLinks?.find((el: ViewerLink) => el.userRole === communication.userRole)?.viewerLink;
                if (callbackResponseLink) {
                    window.location.href = callbackResponseLink;
                }
            }
        } catch (error) {
            setLocalLoader(false);
            ToastNotifyError();
            console.error('Error in callForSignature:', error);
        }
    }

    const goToPrevStepHandler = () => {
        if (isLinear) {
            if (communication?.userRole === UserRoleEnum.USER_1) {
                navigate(composeUrl(token, LINEAR_PATHS.SIGNATURE_PROPOSAL_PAGE, LINEAR_BASE_ROUTE));
            } else if (communication?.userRole === UserRoleEnum.CTP) {
                const baseRoute = `${LINEAR_BASE_ROUTE}/${AUTH_CONFIRM_BASE_ROUTH}`
                const nextPath = composeUrl(token, AUTH_CONFIRM_PATHS.AUTHORIZE_CONFIRM_CAI_PREVIEW, baseRoute)
                navigate(nextPath)
            }
        } else {
            /* istanbul ignore next */
            if (communication?.userRole === UserRoleEnum.USER_1 && caiCaseEnum !== CaiCaseEnum.CASE_2) {
                navigate(composeUrl(token, PATHS.CAI_PREVIEW, LINEAR_BASE_ROUTE));
            } else {
                navigate(composeUrl(token, AUTH_CONFIRM_PATHS.AUTHORIZE_CONFIRM_CAI_PREVIEW, AUTH_CONFIRM_BASE_ROUTH));
            }
        }
    };

    return (
        <>
            <PageContainerCmp
                routePath={(communication?.userRole === UserRoleEnum.USER_1 && caiCaseEnum !== CaiCaseEnum.CASE_2) ? PATHS.CHOOSE_SIGNATURE_AUTHENTICATION : AUTH_CONFIRM_PATHS.AUTHORIZE_CHOOSE_SIGNATURE_AUTHENTICATION}
                removeNextBtn
                forceHideBreadcrumb={communication.type === TemplateTypeEnum.CASE_1B_APP}
            >
                {localLoader && <LoaderCmp className="cover cai-preview" />}
                <>
                    <Section>
                        {isLinear ? <LTitleIconSection style={{ marginLeft: "10px" }}>
                            <LIconContainer><FontAwesomeIcon fontSize={24} icon={faChevronLeft} onClick={goToPrevStepHandler} /></LIconContainer>
                            <LPageTitle style={{ marginLeft: "20px" }}>Scegli il metodo di autenticazione</LPageTitle>
                        </LTitleIconSection> : <PageTitle>Scegli il metodo di autenticazione</PageTitle>}
                        {isLinear ? <Vspace qty={2}></Vspace> : <Vspace></Vspace>}
                        {isLinear ? <LSimpleText>Seleziona il metodo di autenticazione che preferisci utilizzare per firmare la CAI.</LSimpleText> : <TextDefault centered>Seleziona il metodo di autenticazione che preferisci utilizzare per firmare la CAI.</TextDefault>}
                    </Section>
                    <Vspace></Vspace>
                    <Section>
                        <NextButton
                            style={{ width: isLinear ? "260px" : "220px", backgroundColor: "#2469B4", borderRadius: "10px", height: "44px", fontSize: "18px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "15px", paddingRight: "16px", position: "relative" }}
                            onClick={() => callForSignature(SignAuthType.SPID)}
                        >
                            <img src={spidIcon} alt="SPID" style={{ width: "24px", height: "24px", marginRight: !isLinear ? "14px" : '16px' }} />
                            <div style={{ width: "1px", height: "20px", backgroundColor: "white", marginRight: !isLinear ? "20px" : '0px' }}></div>
                            <div style={{ textTransform: "none", ...(isLinear && { width: "100%", marginTop: "3px" }) }}>{isLinear ? "SPID" : "Entra con SPID"}</div>
                        </NextButton>
                        <Vspace qty={2}></Vspace>
                        <NextButton
                            style={{ width: isLinear ? "260px" : "220px", backgroundColor: "#2469B4", borderRadius: "10px", height: "44px", fontSize: "18px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "20px", position: "relative" }}
                            onClick={() => callForSignature(SignAuthType.CIE)}
                        >
                            <img src={cieIcon} alt="CIE" style={{ width: "24px", height: "24px", marginRight: !isLinear ? "14px" : '16px' }} />
                            <div style={{ width: "1px", height: "20px", backgroundColor: "white", marginRight: !isLinear ? "20px" : '0px' }}></div>
                            <div style={{ textTransform: "none", ...(isLinear && { width: "100%", marginTop: "3px" }) }}>{isLinear ? "CIE" : "Entra con CIE"}</div>
                        </NextButton>
                        <Vspace qty={2}></Vspace>

                        {!isLinear ? <SecondChoiseButton
                            style={{ color: "black", fontSize: "14px", lineHeight: "40px" }}
                            onClick={() => callForSignature(SignAuthType.ALTERNATIVE_METHOD)}
                        >Firma con un metodo alternativo</SecondChoiseButton> : <NextButton
                            style={{ width: "260px", backgroundColor: "#2469B4", borderRadius: "10px", height: "44px", fontSize: "18px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "20px", position: "relative" }}
                            onClick={() => callForSignature(SignAuthType.ALTERNATIVE_METHOD)}
                        >
                            <img src={faceId} alt="ALT" style={{ width: "24px", height: "24px", marginRight: '16px' }} />
                            <div style={{ width: "1px", height: "20px", backgroundColor: "white" }}></div>
                            <div style={{ textTransform: "none", ...(isLinear && { width: "100%", marginTop: "3px" }) }}>Metodo alternativo</div>
                        </NextButton>}

                        <Vspace qty={4}></Vspace>

                        {(window.REACT_APP_NODE_ENV === "dev" || window.REACT_APP_NODE_ENV === "sit") && <NextButton
                            style={{ backgroundColor: "	#FFCC00", borderRadius: "10px", height: "40px", fontSize: "18px", fontWeight: 600, lineHeight: "40px", display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "15px", paddingRight: "15px", position: "relative" }}
                            onClick={() => callForSignature(SignAuthType.CLICK_TO_SIGN)}
                        >
                            <FontAwesomeIcon icon={faPenClip} style={{ width: "22px", height: "22px" }} />
                            <div style={{ position: "absolute", left: "46%", top: "50%", transform: "translateY(-50%)", width: "1px", height: "20px", backgroundColor: "white" }}></div>
                            FEA
                        </NextButton>}
                    </Section>
                </>

                {isLinear && <LinearFooterCmp />}

            </PageContainerCmp>
        </>
    )
}

export default ChooseSignatureAuthenticationPage